import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useMediaQuery, useTheme } from '@mui/material'
import { useQuery, useQueryClient } from 'react-query'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'

import ClockIn from './clock-in-layouts/ClockIn'
import ClockOut from './clock-in-layouts/ClockOut'
import LocationError from './clock-in-layouts/LocationError'
import ShiftNotAssigned from './clock-in-layouts/ShiftNotAssigned'
import AdxDrawerComponent from 'components/reusable/drawer/AdxDrawerComponent'
import AdxButton from 'components/custom/AdxButton'

import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'

import getLocation from 'services/api/time-attendance'
import { formatTimeToAmPm } from 'services/helper/DateTime'

const ClockInDrawer = ({
  data,
  shiftStatus,
  hasShift = true,
  shiftZone,
  setSuccessSnackbarOpen,
  setErrorSnackbarOpen,
  setWarningSnackbarOpen,
  time,
  fullWidth = false,
  isPaused,
  setIsPaused,
  showPauseResumeOnButton = false,
  open,
  setOpen,
  suppressPauseResumeToast = true,
  buttonSx,
}) => {
  const normalizedStatus = (shiftStatus || '').toLowerCase()
  const isInactive = normalizedStatus === 'inactive'

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // keep page + internal UI state here
  const [active, setActive] = useState(false)
  const [page, setPage] = useState(1)
  const [stampedClockOutTime, setStampedClockOutTime] = useState(null)

  const [permission, setPermission] = useState(null)
  const [coordinates, setCoordinates] = useState(null)
  const [locationLoading, setLocationLoading] = useState(false)

  const [steps, setSteps] = useState([])
  const [showMap, setShowMap] = useState({})
  const [drawerHeaderVariant, setDrawerHeaderVariant] = useState('default')

  const [internalOpen, setInternalOpen] = useState(false)
  const isDrawerOpen = typeof open === 'boolean' ? open : internalOpen
  const setDrawerOpen = typeof setOpen === 'function' ? setOpen : setInternalOpen

  const resetDrawerState = useCallback(() => {
    setPage(1)
    setPermission(null)
    setCoordinates(null)
    setLocationLoading(false)
    setDrawerHeaderVariant('default')
  }, [])

  const requestLocationIOSFriendly = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error('Geolocation not supported'))

      const opts = { enableHighAccuracy: true, maximumAge: 0 }
      let done = false

      const finish = (fn, val) => {
        if (done) return
        done = true
        fn(val)
      }
      const timer = setTimeout(() => {
        try { navigator.geolocation.clearWatch(watchId) } catch { }
        finish(reject, Object.assign(new Error('Location timeout'), { code: 3 }))
      }, 15000)

      const watchId = navigator.geolocation.watchPosition(
        (pos) => {
          clearTimeout(timer)
          try { navigator.geolocation.clearWatch(watchId) } catch { }
          finish(resolve, pos)
        },
        (err) => {
          // fallback to single shot
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              clearTimeout(timer)
              finish(resolve, pos)
            },
            (e) => {
              clearTimeout(timer)
              finish(reject, e || err)
            },
            { ...opts, timeout: 15000 }
          )
        },
        opts
      )
    })

  const toggleMap = (stepIndex) => {
    setShowMap((prev) => ({ ...prev, [stepIndex]: !prev[stepIndex] }))
  }

  const handleCloseDrawer = useCallback(() => {
    resetDrawerState()
    setDrawerOpen(false)
  }, [resetDrawerState, setDrawerOpen])

  const handleOpenDrawer = () => {
    resetDrawerState()
    setDrawerOpen(true)

    if (!hasShift) {
      setPage(-1)
      // setErrorSnackbarOpen?.('Shift assignment is required to clock in. Please contact your manager.')
      return
    }

    const needsLocation =
      permission !== 'denied' &&
      (!hasClockIn || (page === 2 && !hasClockOut))

    if (needsLocation) {
      getUserLocationRef.current?.()
    }
  }

  const currentDateTime = new Date()
  const day = String(currentDateTime.getDate()).padStart(2, '0')
  const month = String(currentDateTime.getMonth() + 1).padStart(2, '0')
  const year = currentDateTime.getFullYear()
  const formattedDate = `Today, ${year}-${month}-${day}`

  const hasClockIn = !!data?.clockin?.clockin
  const hasClockOut = !!data?.clockout?.clockout

  useEffect(() => {
    if (!isInactive && hasClockIn && !hasClockOut) setActive(true)
    if (!hasClockIn) setActive(false)
    if (hasClockOut) setActive(false)
  }, [hasClockIn, hasClockOut, isInactive])

  const validateLocationAccuracy = useCallback((accuracy) => {
    if (!accuracy) return { valid: true, level: 'unknown' }
    if (accuracy <= 20) return { valid: true, level: 'excellent' }
    if (accuracy <= 50) return { valid: true, level: 'good' }
    if (accuracy <= 100) return { valid: true, level: 'fair' }
    return { valid: true, level: 'poor' }
  }, [])

  useEffect(() => {
    resetDrawerState()
  }, [data?.id, hasShift, shiftStatus, resetDrawerState])

  const getUserLocation = useCallback(async () => {
    if (locationLoading) return
    if (!navigator.geolocation) {
      setErrorSnackbarOpen?.('Location services are not supported by your browser.')
      setPermission('denied')
      setPage(0)
      return
    }

    setLocationLoading(true)

    try {
      const position = await requestLocationIOSFriendly()
      const { latitude, longitude, accuracy } = position.coords
      const validation = validateLocationAccuracy(accuracy)

      setCoordinates({
        latitude,
        longitude,
        accuracy,
        validation,
        timestamp: Date.now(),
      })

      setPermission('granted')
      setLocationLoading(false)
    } catch (error) {
      setLocationLoading(false)
      setCoordinates(null)

      const code = error?.code
      if (code === 1) {
        setPermission('denied')
        setErrorSnackbarOpen?.('Location access is required to clock in. Please allow Location in Safari settings.')
      } else {
        setPermission(null) // unknown/unavailable/timeout
        setErrorSnackbarOpen?.('Unable to get your location. Please try again.')
      }

      setPage(0)
    }
  }, [setErrorSnackbarOpen, validateLocationAccuracy])

  // Build stepper data
  useEffect(() => {
    const stepsArray = []

    if (data?.clockin?.clockin) {
      stepsArray.push({
        label: `${formatTimeToAmPm(data.clockin.clockin)} • Clock in`,
        description: data?.clockin?.street,
        note: data?.clockin?.remarks,
        coordinates: {
          latitude: data?.clockin?.latitude,
          longitude: data?.clockin?.longitude,
        },
        hasMap: true,
      })
    }

    if (data?.clockout?.clockout) {
      stepsArray.push({
        label: `${formatTimeToAmPm(data.clockout.clockout)} • Clock out`,
        description: data?.clockout?.street || '',
        note: data?.clockout?.remarks,
        coordinates: {
          latitude: data?.clockout?.latitude,
          longitude: data?.clockout?.longitude,
        },
        hasMap: true,
      })
    }

    setSteps(stepsArray)
  }, [data])

  const shouldGeocode = useMemo(() => {
    const hasLatLon = coordinates?.latitude != null && coordinates?.longitude != null
    if (!hasLatLon) return false

    if (hasClockIn && !hasClockOut && page !== 2) return false
    if (!hasClockIn) return true
    if (page === 2 && !hasClockOut) return true

    return false
  }, [coordinates?.latitude, coordinates?.longitude, hasClockIn, hasClockOut, page])

  const { isLoading: locationIsLoading, data: locationData, isError: locationIsError } = useQuery(
    ['getLocation', coordinates?.latitude, coordinates?.longitude],
    async () => {
      const res = await getLocation(coordinates?.latitude, coordinates?.longitude)
      return res
    },
    {
      refetchOnWindowFocus: false,
      enabled: shouldGeocode,
    }
  )

  const shouldAutoFetchLocation = useMemo(() => {
    if (!isDrawerOpen || permission === 'denied' || !hasShift) return false

    if (!hasClockIn) return true

    if (page === 2 && !hasClockOut) return true

    return false
  }, [isDrawerOpen, permission, hasClockIn, hasClockOut, page, hasShift])

  const getUserLocationRef = useRef(getUserLocation)
  useEffect(() => {
    getUserLocationRef.current = getUserLocation
  }, [getUserLocation])

  const handleDrawerComponentClose = useCallback(() => {
    return () => {
      handleCloseDrawer()
    }
  }, [handleCloseDrawer])

  useEffect(() => {
    if (shouldAutoFetchLocation) {
      getUserLocationRef.current?.()
    }
  }, [shouldAutoFetchLocation])

  const layoutProps = {
    setPage,
    page,
    data,
    active,
    setActive,
    time,
    isPaused,
    setIsPaused,
    stampedClockOutTime,
    setStampedClockOutTime,
    permission,
    coordinates,
    setCoordinates,
    locationLoading,
    getUserLocation,
    locationIsLoading,
    locationIsError,
    locationData,
    steps,
    showMap,
    setShowMap,
    toggleMap,
    setSuccessSnackbarOpen,
    setErrorSnackbarOpen,
    setWarningSnackbarOpen,
    currentDateTime,
    suppressPauseResumeToast,
    closeDrawer: handleCloseDrawer,
    setDrawerOpen,
    shiftZone,
    setDrawerHeaderVariant,
  }

  const queryClient = useQueryClient()

  const renderLayout = () => {
    switch (page) {
      case -1:
        return (
          <ShiftNotAssigned
            onRetry={async () => {
              // Refetch attendance data in case shift was just assigned
              await queryClient.invalidateQueries(['getAttendanceDashboard'])
              // Only proceed if shift is now available, otherwise close drawer
              if (hasShift) {
                setPage(1)
                getUserLocationRef.current?.()
              } else {
                handleCloseDrawer()
              }
            }}
          />
        )
      case 0:
        return <LocationError props={layoutProps} />
      case 1:
        return <ClockIn props={layoutProps} />
      case 2:
        return <ClockOut props={layoutProps} />
      default:
        return <ClockIn props={layoutProps} />
    }
  }

  const getMainButtonConfig = () => {
    if (isInactive || (hasClockIn && hasClockOut)) {
      return {
        label: active ? 'Clock out' : 'Clock in',
        icon: <TimerOutlinedIcon />,
        disabled: true,
        title: isInactive ? 'Shift is inactive' : undefined,
      }
    }

    if (!hasClockIn) {
      return {
        label: 'Clock in',
        icon: <TimerOutlinedIcon />,
        disabled: false,
        title: undefined,
      }
    }

    if (showPauseResumeOnButton) {
      return {
        label: isPaused ? 'Resume' : 'Pause',
        icon: isPaused ? <PlayArrowIcon /> : <PauseIcon />,
        disabled: false,
        title: undefined,
      }
    }

    return {
      label: 'Clock out',
      icon: <TimerOutlinedIcon />,
      disabled: false,
      title: undefined,
    }
  }

  const btn = getMainButtonConfig()

  return (
    <>
      <AdxButton
        onClick={handleOpenDrawer}
        disabled={btn.disabled}
        title={btn.title}
        disableElevation
        disableRipple
        disableFocusRipple
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          width: '100%',
          minWidth: 0,
          height: 45,
          px: 2.5,
          borderRadius: '100px',
          textTransform: 'none',
          boxShadow: 'none',

          '&:hover': { boxShadow: 'none' },
          '&.Mui-focusVisible': { boxShadow: 'none' },


          ...(buttonSx || {}),
        }}
      >
        {btn.icon}
        {btn.label}
      </AdxButton>


      <AdxDrawerComponent
        state={{ right: isDrawerOpen }}
        anchor="right"
        onClose={handleDrawerComponentClose}
        headerprops={drawerHeaderVariant === 'outsideZone' ? false : true}
        title={page === 0 || drawerHeaderVariant === 'outsideZone' ? null : formattedDate}
        sx={{ width: isMobile ? '100vw' : '450px' }}
        sxHeader={{
          borderBottom: page === 0 || drawerHeaderVariant === 'outsideZone' ? 'none' : '1px solid',
          // Logic to apply space-between
          justifyContent: (page !== 0 && drawerHeaderVariant !== 'outsideZone') 
            ? 'space-between' 
            : (drawerHeaderVariant === 'outsideZone' ? 'flex-start' : 'flex-end'),
          // flexDirection: 'row-reverse', 
        }}
      >
        {renderLayout()}
      </AdxDrawerComponent>
    </>
  )
}




export default ClockInDrawer
